import React from 'react';
import { formatCurrency } from '../utils/formatter';

function ScenarioTable({ forecastData }) {
  if (!forecastData || !forecastData.targets) {
    return null;
  }

  const getRevenueAtDate = (data, date) => {
    const dataPoint = data.find(d => d.month === date);
    return dataPoint ? dataPoint.cumulativeRevenue : 0;
  };

  const getStatus = (revenue, target) => {
    if (revenue >= target) return { text: 'On Track', color: 'text-green-600 bg-green-100' };
    if (revenue >= target * 0.9) return { text: 'At Risk', color: 'text-yellow-600 bg-yellow-100' };
    return { text: 'Behind', color: 'text-red-600 bg-red-100' };
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold mb-4">Scenario Comparison</h3>

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Target</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Goal</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Worst Case</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Base Case</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Best Case</th>
              <th className="text-center py-3 px-4 font-semibold text-gray-700">Base Status</th>
            </tr>
          </thead>
          <tbody>
            {forecastData.targets.map((target, idx) => {
              const worstRevenue = getRevenueAtDate(forecastData.worst, target.date);
              const baseRevenue = getRevenueAtDate(forecastData.base, target.date);
              const bestRevenue = getRevenueAtDate(forecastData.best, target.date);
              const status = getStatus(baseRevenue, target.value);

              return (
                <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium">{target.name}</td>
                  <td className="py-3 px-4 text-gray-600">{target.date}</td>
                  <td className="py-3 px-4 text-right font-semibold">{formatCurrency(target.value)}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="font-medium text-red-600">{formatCurrency(worstRevenue)}</div>
                    <div className="text-xs text-gray-500">
                      {worstRevenue >= target.value
                        ? `+${formatCurrency(worstRevenue - target.value)}`
                        : `-${formatCurrency(target.value - worstRevenue)}`
                      }
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="font-medium text-blue-600">{formatCurrency(baseRevenue)}</div>
                    <div className="text-xs text-gray-500">
                      {baseRevenue >= target.value
                        ? `+${formatCurrency(baseRevenue - target.value)}`
                        : `-${formatCurrency(target.value - baseRevenue)}`
                      }
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="font-medium text-green-600">{formatCurrency(bestRevenue)}</div>
                    <div className="text-xs text-gray-500">
                      {bestRevenue >= target.value
                        ? `+${formatCurrency(bestRevenue - target.value)}`
                        : `-${formatCurrency(target.value - bestRevenue)}`
                      }
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${status.color}`}>
                      {status.text}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-gray-500">
        <div className="flex items-center justify-between">
          <span>Current Revenue: {formatCurrency(forecastData.currentRevenue)}</span>
          <div className="flex gap-4">
            <span className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              Worst Case
            </span>
            <span className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              Base Case
            </span>
            <span className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              Best Case
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScenarioTable;
